import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Container, Typography, Paper, Chip, CircularProgress, Alert,
  Button, Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useNavigate } from 'react-router-dom';
import { format, parseISO, isValid } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { listMyQueue, CiQueueItem } from '../services/supabase/ci.service';

const statusColor = (s: string): 'success' | 'warning' | 'default' =>
  s === 'submitted' ? 'success' : s === 'in_progress' ? 'warning' : 'default';

const peso = (n?: number | null) =>
  n == null ? '—' : `₱${Number(n).toLocaleString('en-PH', { minimumFractionDigits: 0 })}`;

const CiQueue: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  const [items, setItems] = useState<CiQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const officerId = userProfile?.id;

  const load = useCallback(async () => {
    if (!officerId) return;
    setLoading(true);
    try {
      setItems(await listMyQueue(officerId));
    } catch (err: any) {
      console.error('Error loading CI queue:', err);
      setError(err.message || 'Failed to load your CI queue.');
    } finally {
      setLoading(false);
    }
  }, [officerId]);

  useEffect(() => { load(); }, [load]);

  const pending = items.filter((i) => i.investigation.status !== 'submitted');
  const done = items.filter((i) => i.investigation.status === 'submitted');

  const renderCard = (item: CiQueueItem) => {
    const { investigation: inv, applicant } = item;
    return (
      <Paper
        key={inv.id}
        variant={isMobile ? 'outlined' : 'elevation'}
        elevation={isMobile ? 0 : 2}
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: 1.5,
          borderRadius: 2,
          cursor: 'pointer',
        }}
        onClick={() => navigate(`/ci/${inv.id}`)}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: isMobile ? 15 : 16 }}>
              {applicant?.full_name || `${applicant?.first_name ?? ''} ${applicant?.last_name ?? ''}`.trim() || 'Unknown applicant'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {applicant?.current_address || 'No address on file'}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={inv.status.replace('_', ' ')}
            color={statusColor(inv.status)}
          />
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">
            {applicant?.product_name || 'No product'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {peso(applicant?.requested_amount)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Assigned {inv.assigned_at && isValid(parseISO(inv.assigned_at))
              ? format(parseISO(inv.assigned_at), 'MMM dd')
              : '—'}
          </Typography>
        </Box>
      </Paper>
    );
  };

  return (
    <Container
      maxWidth="md"
      sx={{ py: { xs: 2, sm: 3 }, px: { xs: 1.5, sm: 3 }, overflowX: 'hidden' }}
    >
      <Typography
        variant={isMobile ? 'h5' : 'h4'}
        gutterBottom
        sx={{ fontWeight: 700, mb: { xs: 1.5, sm: 2 } }}
      >
        Credit Investigation
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Field visits assigned to you. Tap a card to record your findings.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>
      ) : items.length === 0 ? (
        <Alert severity="info">
          You have no credit investigations assigned. The office assigns these from the applicant's
          record.
        </Alert>
      ) : (
        <>
          {pending.length > 0 && (
            <>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                To do ({pending.length})
              </Typography>
              <Box sx={{ mt: 1, mb: 3 }}>{pending.map(renderCard)}</Box>
            </>
          )}
          {done.length > 0 && (
            <>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
                Submitted ({done.length})
              </Typography>
              <Box sx={{ mt: 1 }}>{done.map(renderCard)}</Box>
            </>
          )}
        </>
      )}

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Button onClick={load} disabled={loading} size="small">Refresh</Button>
      </Box>
    </Container>
  );
};

export default CiQueue;
